/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */


import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Promise from 'bluebird';
import { compose, extend, result } from 'underscore';
import dataContextManager from '@bingads-webui-static-legacy/summary/SummaryDataContextManager';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { fetchInitData, getPendingImportStatusPromise } from './repository';
import { loadLegacyDeps, createCommonDeps } from './utils';
import { getFileImportStyle } from './styles/file-import-page-style';

class FileImport extends React.PureComponent {
  constructor(props) {
    super(props);
    this.perfMarker = props.perfMarker;
    this.state = { FileImportPageView: null };
    this.initialize(props);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.scope.values.importId !== this.props.scope.values.importId) {
      this.initialize(this.props);
    }
  }

  initialize(props) {
    this.perfMarker.willFetchData();
    const fetchInitDataPromise = fetchInitData(props.scope.values, false);

    Promise.all([
      fetchInitDataPromise,
      getPendingImportStatusPromise(),
    ]).then(([data, importStatus]) => {
      const deps = this.createDeps(data, props, importStatus);
      return loadLegacyDeps(props, false).then(spaFactory => spaFactory.initFileImportPage(deps).render());
    })
      .then((instance) => {
        instance.startPollingAfterContinueImport();
        this.setState({
          FileImportPageView: (<BackboneInstanceWrapper instance={instance} />),
        });
      });
  }

  createDeps(data, props, importStatus) {
    const deps = createCommonDeps({
      ...props,
      data,
    });

    let importTemplateFileName = 'ImportTemplateWithAdExtensions.csv';
    const IsEnhancedShoppingCampaignAllowed = result(props.permissions, 'IsEnhancedShoppingCampaignAllowed', false);
    const IsDSAMixedModeCampaignEnabled = result(props.permissions, 'IsDSAMixedModeCampaignEnabled', false);

    if (IsDSAMixedModeCampaignEnabled) {
      if (IsEnhancedShoppingCampaignAllowed) {
        importTemplateFileName = 'ImportTemplateWithAdExtensionsAndBSCAndAdGroupType.csv';
      } else {
        importTemplateFileName = 'ImportTemplateWithAdExtensionsAndAdGroupType.csv';
      }
    } else if (IsEnhancedShoppingCampaignAllowed) {
      importTemplateFileName = 'ImportTemplateWithAdExtensionsAndBSC.csv';
    } else {
      importTemplateFileName = 'ImportTemplateWithAdExtensions.csv';
    }

    extend(deps, {
      importStatus,
      importId: props.scope.values.importId,
      notMappedValue: data.notMappedValue,
      defaultColumns: data.defaultColumns,
      perfMarker: this.perfMarker,
      importTemplateUrl: `${props.serverContext.TemplatesPathUrl}Import/${importTemplateFileName}`,
      dataContextManager,
    });

    return deps;
  }

  render() {
    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <div className={classNames(this.props.classes.pageContainer, this.props.classes.fileImportPage, 'file-import-page-container')}>
          {this.state.FileImportPageView &&
          <React.Fragment>
            {this.state.FileImportPageView}
          </React.Fragment>
          }
        </div>
      </React.Suspense>
    );
  }
}

FileImport.propTypes = {
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.object.isRequired,
    CurrentCustomer: PropTypes.object.isRequired,
    TemplatesPathUrl: PropTypes.string.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    pageContainer: PropTypes.string.isRequired,
    fileImportPage: PropTypes.string.isRequired,
  }).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    IsUserContentAllowed: PropTypes.bool.isRequired,
    IsEnhancedShoppingCampaignAllowed: PropTypes.bool.isRequired,
  }).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
};

export const StyledFileImport = compose(
  withI18nConsumer,
  WrappedComponent => withSiteMap(withDefaultStyles(WrappedComponent, getFileImportStyle))
)(FileImport);
